"use client";

import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import {
  FaChevronDown, FaRegEye, FaUserTie, FaShieldAlt, FaBolt, FaSun, FaArrowRight,
  FaCode, FaGlobe, FaMobileAlt, FaCloud, FaRocket, FaCheck, FaStar,
  FaHeart, FaAtom, FaLightbulb, FaHandshake, FaLeaf, FaBrain,
} from 'react-icons/fa';
import {
  LuLayoutGrid, LuActivity, LuRocket, LuBuilding, LuCpu,
  LuGraduationCap, LuTrendingUp, LuShoppingCart, LuCheck,
  LuAtom, LuStar, LuHeart, LuZap, LuCode, LuGlobe, LuSmartphone,
  LuCloud, LuBrain, LuSettings, LuUsers, LuShield, LuDatabase,
  LuTarget, LuPlane, LuGem, LuBookOpen, LuAward, LuFlag,
} from 'react-icons/lu';

// Named lookup maps for dynamic icon rendering
const LuIconsMap = {
  LuLayoutGrid, LuActivity, LuRocket, LuBuilding, LuCpu,
  LuGraduationCap, LuTrendingUp, LuShoppingCart, LuCheck,
  LuAtom, LuStar, LuHeart, LuZap, LuCode, LuGlobe, LuSmartphone,
  LuCloud, LuBrain, LuSettings, LuUsers, LuShield, LuDatabase,
  LuTarget, LuPlane, LuGem, LuBookOpen, LuAward, LuFlag,
};
const FaIconsMap = {
  FaUserTie, FaShieldAlt, FaBolt, FaSun, FaArrowRight,
  FaCode, FaGlobe, FaMobileAlt, FaCloud, FaRocket, FaCheck, FaStar,
  FaHeart, FaAtom, FaLightbulb, FaHandshake, FaLeaf, FaBrain,
};
import { motion, useScroll, useTransform } from 'framer-motion';
import { Image } from 'react-bootstrap';
import Link from 'next/link';
import './about.css';
import AboutBanner from './AboutBanner';
import BrandsSlider from '../../components/BrandsSlider';

// --- DATA ARRAYS (FALLBACKS) ---
const words = [
  'Customer Support', 'Passion', 'Positive Experience', 'Trustworthiness',
  'Customer Focus', 'Long-term Relation', 'Innovation', 'Collaboration',
  'Personalization', 'Reliability', 'Quality',
];

const timelineData = [
  {
    step: '01',
    tag: 'step 01',
    title: 'Discovery Phase',
    description: 'During this initial stage, we delve deep into understanding your business goals, target audience, and market landscape. We conduct thorough research and analysis to identify opportunities and challenges, laying the foundation for a successful campaign.',
  },
  {
    step: '02',
    tag: 'step 02',
    title: 'Strategy Development',
    description: 'With insights gathered from the discovery phase, we develop a customized strategy tailored to your specific needs and objectives. This comprehensive plan outlines the key tactics, channels, and timelines needed to achieve your business goals effectively.',
  },
  {
    step: '03',
    tag: 'step 03',
    title: 'Implementation and Execution',
    description: 'Once the strategy is finalized and approved, we roll up our sleeves and put the plan into action. Our expert team utilizes cutting-edge tools and techniques to execute each component of the strategy with precision and efficiency.',
  },
  {
    step: '04',
    tag: 'step 04',
    title: 'Monitoring and Optimization',
    description: 'Continuous monitoring and optimization are essential to ensuring the success and effectiveness of our campaigns. We closely track key performance indicators and adjust the strategy accordingly.',
  },
];

const faqData = [
  {
    question: 'What services does your company offer?',
    answer: 'We offer a wide range of IT services, including website development, mobile app development, digital marketing, software development, staff augmentation, and UI/UX design.',
  },
  {
    question: 'Do you work with startups as well as established enterprises?',
    answer: 'Yes, we cater to both startups and established businesses. Our flexible service models are designed to meet the specific needs of clients, regardless of their size.',
  },
  {
    question: 'Can you handle international projects?',
    answer: 'Yes, we have experience working with clients globally, and we ensure seamless communication and project management across different time zones.',
  },
  {
    question: 'What platforms do you use for website development?',
    answer: 'We develop websites using modern platforms and frameworks such as Laravel, CodeIgniter, Shopify, WordPress, and the MERN stack (MongoDB, Express.js, React, Node.js).',
  },
  {
    question: 'Will the website be optimized for search engines (SEO)?',
    answer: 'Yes, we ensure SEO optimization with proper meta tags, fast loading speeds, mobile responsiveness, and schema markup to improve search engine rankings.',
  },
  {
    question: 'Do you develop apps for both Android and iOS?',
    answer: 'Yes, we develop native apps for both Android and iOS, as well as cross-platform apps using Flutter and React Native for faster development and lower costs.',
  },
  {
    question: 'How do you measure the success of digital marketing campaigns?',
    answer: 'We use key performance indicators (KPIs) such as website traffic, conversion rates, click-through rates (CTR), and return on investment (ROI) to measure the success of our campaigns.',
  },
];

const premiumValuesData = [
  {
    icon: <FaUserTie />,
    title: 'Customer Centric',
    description: "Our primary focus is on our clients' needs. We emphasise identifying their specific obstacles and goals, modifying our strategies to offer personalised service."
  },
  {
    icon: <FaShieldAlt />,
    title: 'Accuracy',
    description: 'We are committed to nurturing robust alliances. Through close collaboration with our clients, we establish cooperative connections based on transparent communication and mutual esteem.'
  },
  {
    icon: <FaBolt />,
    title: 'Innovation',
    description: 'Demands and preferences of customers are always evolving, necessitating a creative approach to provide value and cater to their requirements.'
  },
  {
    icon: <FaSun />,
    title: 'Maintaining Simplicity',
    description: 'We prioritize ethical standards, transparency, and authenticity in all interactions. Our operations are built on integrity.'
  }
];

// --- DYNAMIC UTILITIES ---
const getImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";

  if (url.includes("/uploads/")) {
    const cleanPath = url.substring(url.indexOf("/uploads/"));
    return `${apiUrl}${cleanPath}`;
  }

  if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${apiUrl}${cleanPath}`;
  }

  if (url.startsWith("http")) return url;
  return url;
};

const renderIcon = (iconName) => {
  if (!iconName) return <FaUserTie />;

  // 1. Try to find Lucide Icon in react-icons/lu (prefixed with 'Lu')
  const luName = `Lu${iconName}`;
  const LuComponent = LuIconsMap[luName];
  if (LuComponent) {
    return <LuComponent />;
  }

  // 2. Try to find in react-icons/fa (prefixed with 'Fa')
  const faName = `Fa${iconName}`;
  const FaComponent = FaIconsMap[faName];
  if (FaComponent) {
    return <FaComponent />;
  }

  // 3. Try exact match in react-icons/fa
  const ExactFaComponent = FaIconsMap[iconName];
  if (ExactFaComponent) {
    return <ExactFaComponent />;
  }

  // 4. Default fallback icon
  return <FaUserTie />;
};

const ValueCard = ({ index, icon, title, description }) => {
  const cardRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start start", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.95 - index * 0.01]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.6]);
  const blur = useTransform(
    scrollYProgress,
    [0, 1],
    ["blur(0px)", "blur(3px)"]
  );

  return (
    <motion.div
      ref={cardRef}
      style={{
        scale,
        opacity,
        filter: blur,
        position: 'sticky',
        top: `${140 + index * 24}px`,
        zIndex: 10 + index,
        transformOrigin: 'top center',
      }}
      className="refined-value-card-wrapper"
    >
      <div className="refined-value-card">
        <div className="refined-icon-circle">
          {icon}
        </div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </motion.div>
  );
};

export default function AboutClient({ initialAboutData }) {
  const [aboutActiveIndex, setAboutActiveIndex] = useState(null);
  const toggleAboutAccordion = (index) => {
    setAboutActiveIndex(aboutActiveIndex === index ? null : index);
  };

  const [faqActiveIndex, setFaqActiveIndex] = useState(null);
  const toggleFaqAccordion = (index) => {
    setFaqActiveIndex(faqActiveIndex === index ? null : index);
  };

  const circleRef = useRef(null);
  const timelineRef = useRef(null);
  const trackRef = useRef(null);
  const [visibleItems, setVisibleItems] = useState([]);

  // State for fetched about page data
  const [aboutData, setAboutData] = useState(initialAboutData || null);

  // Fetch about page data on load
  useEffect(() => {
    if (initialAboutData) return;

    const fetchAboutData = async () => {
      try {
        const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
        const res = await fetch(`${apiUrl}/api/about/main-about-page`, { cache: 'no-store' });
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data) {
            setAboutData(json.data);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch dynamic about page data. Falling back to static values.");
      }
    };
    fetchAboutData();
  }, []);

  // Ensure AOS refreshes AFTER React renders the new dynamic data
  useEffect(() => {
    if (aboutData) {
      setTimeout(() => {
        if (typeof window !== "undefined" && window.AOS) {
          window.AOS.refreshHard();
        } else {
          import('aos').then((mod) => {
            mod.default.refreshHard();
          });
        }
      }, 100); // Wait for DOM to paint
    }
  }, [aboutData]);

  useEffect(() => {
    const animateWords = () => {
      const wordElements = document.querySelectorAll('.word');
      wordElements.forEach((word, index) => {
        setTimeout(() => {
          word.style.opacity = '1';
          word.style.transform = 'translateY(0)';
        }, index * 300);
      });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateWords();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 },
    );
    if (circleRef.current) {
      observer.observe(circleRef.current);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const viewPoint = scrollTop + window.innerHeight * 0.5;
      const items = document.querySelectorAll('.timeline__item');
      const visible = [];
      items.forEach((item, idx) => {
        const top = item.offsetTop;
        const bottom = top + item.offsetHeight;
        if (viewPoint > top && viewPoint < bottom) {
          visible.push(idx);
        } else if (viewPoint > bottom) {
          visible.push(idx);
        }
      });
      setVisibleItems(visible);
      updateTrack();
    };
    const updateTrack = () => {
      if (!trackRef.current || !timelineRef.current) return;
      const timelineTop = timelineRef.current.offsetTop;
      const scrollY = window.scrollY + window.innerHeight * 0.5;
      const sectionHeight = timelineRef.current.offsetHeight;
      const desiredHeight = scrollY - timelineTop;
      const finalHeight = Math.min(desiredHeight, sectionHeight);
      trackRef.current.style.height = `${finalHeight > 0 ? finalHeight : 0}px`;
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    }
  }, []);

  // --- Dynamic Mapping & Fallbacks ---

  // 1. Who We Are Accordions
  const displayAccordions = aboutData?.aboutCompany?.accordions && aboutData.aboutCompany.accordions.length > 0
    ? aboutData.aboutCompany.accordions
    : [
      { title: "Our History", description: "CodeQlik began its journey in 2018 with a vision to revolutionize the digital landscape. Founded by a team of passionate tech enthusiasts, our company started as a small venture focused on delivering creative web solutions to local businesses. Over the years, CodeQlik has grown into a trusted name in the industry, driven by a commitment to quality, innovation, and customer satisfaction. Today, we take pride in being a digital partner for businesses of all sizes, helping them achieve success in an ever-evolving technological world." },
      { title: "Our Mission", description: "At CodeQlik, our mission is to empower businesses by delivering innovative, reliable, and high-quality digital solutions. We strive to: 1. Enhance Digital Experiences: Create user-centric web and app solutions that combine seamless functionality with exceptional design. 2. Drive Business Growth: Equip businesses with cutting-edge technology and strategic marketing to achieve their goals and expand their reach. 3. Foster Innovation: Continuously explore emerging technologies to provide future-ready solutions. 4. Build Lasting Relationships: Prioritize customer satisfaction by maintaining transparency, trust, and excellence in every project." },
      { title: "Our Vision", description: "At CodeQlik, our vision is to be a global leader in providing innovative, sustainable, and impactful digital solutions that shape the future of businesses. We aim to: 1. Transform Industries: Lead the way in redefining how businesses interact with technology, setting new standards of excellence and innovation. 2. Empower Businesses Worldwide: Help companies of all sizes and industries unlock their full potential through customized, forward-thinking digital strategies. 3. Foster Digital Innovation: Continuously explore and implement emerging technologies to create solutions that are not just relevant today but also future-proof. 4. Be a Trusted Partner: Build lasting relationships with our clients by providing exceptional service, transparency, and measurable results." }
    ];

  // 2. Who We Are Right Content Paragraphs
  const descriptionPara = aboutData?.aboutCompany?.description;
  const rightParas = aboutData?.aboutCompany?.rightContent
    ? aboutData.aboutCompany.rightContent.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
    : [];

  const combinedParas = [];
  if (descriptionPara) {
    combinedParas.push(descriptionPara);
  }
  combinedParas.push(...rightParas);

  const rightParagraphs = combinedParas.length > 0
    ? combinedParas
    : [
      "At CodeQlik, we specialize in delivering cutting-edge web and app development solutions tailored to meet the dynamic needs of businesses. With a passion for innovation and a commitment to excellence, we craft digital experiences that not only look stunning but also perform flawlessly.",
      "At CodeQlik, we believe in empowering businesses with technology, ensuring seamless user experiences, and driving measurable results. With a team of seasoned professionals, we are your partners in building a strong digital foundation for your brand.",
      "Let's elevate your digital presence. Choose CodeQlik for innovation that counts!"
    ];

  // 3. Our Vision (Premium Values Section)
  const displayVisionTitle = aboutData?.ourVision?.title || "Our Values";
  const displayVisionDesc = aboutData?.ourVision?.description || "We partner with you to create a strong and successful business model that reflects your unique vision and supports your growth goals. Our focus is on building effective digital solutions that improve user experience, increase engagement, and drive better conversions for your business.";
  const displayVisionButton = aboutData?.ourVision?.buttons && aboutData.ourVision.buttons[0]
    ? aboutData.ourVision.buttons[0]
    : { text: "Get Started", link: "/contact" };

  const displayVisionCards = aboutData?.ourVision?.cards && aboutData.ourVision.cards.length > 0
    ? aboutData.ourVision.cards.map((c, i) => ({
      icon: renderIcon(c.icon),
      title: c.title,
      description: c.description
    }))
    : premiumValuesData;

  // 4. Values (Spinning Words)
  const displayValuesTitle = aboutData?.valuesSection?.title || "VALUES";
  const displayValuesTags = aboutData?.valuesSection?.tag && aboutData.valuesSection.tag.length > 0
    ? aboutData.valuesSection.tag.map(t => ({ text: t.text, icon: t.icon }))
    : words.map(w => ({ text: w, icon: "Atom" }));

  // 5. Timeline (Process Section)
  const displayProcessTitle = aboutData?.processSection?.title || "Our 4 Stage Process";
  const displayProcessSteps = aboutData?.processSection?.steps && aboutData.processSection.steps.length > 0
    ? aboutData.processSection.steps.map((s, idx) => ({
      step: s.stepNumber || `0${idx + 1}`,
      tag: `step ${s.stepNumber || `0${idx + 1}`}`,
      title: s.title,
      description: s.description
    }))
    : timelineData;

  // 6. Gallery Section
  const displayGalleryTitle = aboutData?.gallerySection?.title || "Our Company Gallery";
  const displayGalleryDesc = aboutData?.gallerySection?.description || "From team outings and office shenanigans to special events and celebrations, these photos capture the essence of our vibrant culture and the bonds that unite us.";
  const fallbackGalleryImages = [
    { url: "/assets/img/gallery.avif" },
    { url: "/assets/img/gallery2.avif" },
    { url: "/assets/img/gallery3.avif" },
    { url: "/assets/img/gallery4.avif" },
    { url: "/assets/img/gallery5.avif" },
    { url: "/assets/img/gallery6.avif" },
  ];
  const dbGalleryImages = aboutData?.gallerySection?.images || [];
  const displayGalleryImages = [...dbGalleryImages];
  for (let i = displayGalleryImages.length; i < 6; i++) {
    displayGalleryImages.push(fallbackGalleryImages[i % 6]);
  }

  // 7. FAQ Section
  const displayFaqTitle = aboutData?.faqSection?.title || "Got Questions We've Got Answers!";
  const displayFaqs = aboutData?.faqSection?.faqs && aboutData.faqSection.faqs.length > 0
    ? aboutData.faqSection.faqs
    : faqData;

  return (
    <div>
      <div className="container py-4 max-w-[1440px] mx-auto">
        <AboutBanner data={aboutData?.banner} />
      </div>

      <section className='about-bg'>
        <div className='container-fluid'>
          <div className='row justify-content-center'>
            <div className='col-12' data-aos='fade-up' data-aos-delay='0'>
              <div className='why-header'>
                <span className='mt-2 orange-pill-tag'>{"Who We Are"}</span>
                <h2>{aboutData?.aboutCompany?.title || "About Us"}</h2>
              </div>
            </div>

            {/* Right content column is placed first in mobile DOM via Bootstrap order classes, so Title + Intro comes first on mobile */}
            <div className='col-lg-5 col-md-12 fixed-column fixedheigth order-1 order-lg-2'>
              <div>
                <div className='we-are-text'>
                  {rightParagraphs.map((para, idx) => (
                    <p key={idx} className={`ptext${idx + 1}`}>{para}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Accordion column is placed second in mobile DOM, and shows on the left side on desktop */}
            <div className='col-lg-6 col-md-12 py-10 scrollable-column order-2 order-lg-1' data-aos='fade-up' data-aos-delay='100'>
              <div className='accordionB'>
                {displayAccordions.map((accordion, idx) => (
                  <div key={idx} className={`accordion-itemA${aboutActiveIndex === idx ? ' active' : ''}`}>
                    <div className='accordion-header' onClick={() => toggleAboutAccordion(idx)}>
                      <h2>{`0${idx + 1}. ${accordion.title}`}</h2>
                      <span className='caret-box'>
                        <i className={`fa-solid ${aboutActiveIndex === idx ? 'fa-minus' : 'fa-plus'}`}></i>
                      </span>
                    </div>
                    <div className='accordion-content'>
                      <p>{accordion.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandsSlider
        title="Brands we have worked with"
        subtitle="BRANDS"
        className="clients"
      />

      {/* Premium Values Section - Refined Black Theme */}
      <section className='values-premium-section'>
        <div className='container'>
          <div className='refined-vision-tag orange-pill-tag'>
            OUR VISION
          </div>
          <div className='row'>
            {/* Left Column - Sticky Content */}
            <div className='col-lg-6'>
              <div className='sticky-left'>
                <h2 className=''>{displayVisionTitle}</h2>
                <p className='desc'>
                  {displayVisionDesc}
                </p>
              </div>
            </div>

            {/* Right Column - Scrollable Cards */}
            <div className='col-lg-6'>
              <div className='value-cards-stack'>
                {displayVisionCards.map((card, i) => (
                  <ValueCard
                    key={i}
                    index={i}
                    icon={card.icon}
                    title={card.title}
                    description={card.description}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='values'>
        <div className='values-section' data-aos='fade-up' data-aos-delay='0'>
          <div className='background-text'>{displayValuesTitle.toUpperCase()}</div>
          <div className='box-mainA'>
            <div className='box-main'>
              <div className='circle-container' ref={circleRef}>
                {displayValuesTags.slice(0, 11).map((tagItem, index) => (
                  <div key={index} className={`word word-${index + 1}`}>
                    <span className="word-icon" style={{ display: 'inline-flex', alignItems: 'center', fontSize: '18px', transform: 'rotate(15deg)' }}>
                      {renderIcon(tagItem.icon || "Atom")}
                    </span>
                    {tagItem.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mt-30" data-aos="fade-up" data-aos-delay={0}>
        <div className="text-center my-5">
          <span className="we-work-tag orange-pill-tag">How We Work</span>
          <h2 className="mt-2 text-white">{displayProcessTitle}</h2>
        </div>
      </div>

      <section className="timeline-section">
        <div className="medium-container">
          <div className="timeline" ref={timelineRef}>
            {displayProcessSteps.map((item, idx) => (
              <div key={idx} className={`timeline__item ${visibleItems.includes(idx) ? 'is-visible' : ''}`}>
                <div className='timeline__line'></div>
                <div className='timeline__wrap'>
                  <div className={idx % 2 === 0 ? 'timeline-step' : 'timeline-step2'}>{item.step}</div>
                  <span className='step-tag orange-pill-tag'>{item.tag}</span>
                  <h5>{item.title}</h5>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
            <div className='timeline__track' ref={trackRef}></div>
          </div>
        </div>
      </section>
    </div>
  );
}